import { useCallback, useEffect, useState } from 'react';
import type { Customer } from '@visionqc/shared';
import { api } from '../api/client';
import { CustomerPill } from '../components/CustomerPill';
import { useIsAdmin } from '../api/auth';
import { CustomerDetailView } from './CustomerDetail';
import { CustomerOnboardWizard } from './CustomerOnboardWizard';

/**
 * Customers — the companies, as opposed to the people who sign in.
 *
 * Deliberately its own section rather than nested under users. They answer
 * different questions: a user is about access and includes internal staff who
 * belong to no company, while a customer is a commercial relationship that may
 * have no users at all when we operate their line for them. They link; they do
 * not contain each other.
 */
export function CustomersView() {
  const isAdmin = useIsAdmin();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [wizard, setWizard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCustomers(await api.listCustomers());
      setError(null);
    } catch (e) {
      // A failed load is surfaced, not turned into a silent empty list — an
      // empty table reads as "no customers", which is a different, alarming fact.
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Customers</h1>
        <div className="panel muted">Managing customers requires the ADMIN role.</div>
      </div>
    );
  }

  if (open !== null) {
    return (
      <CustomerDetailView
        id={open}
        onBack={() => {
          setOpen(null);
          void load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 className="view-title">Customers</h1>
        <button className="btn primary" onClick={() => setWizard(true)}>
          + New customer
        </button>
      </div>
      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}

      {wizard && (
        <CustomerOnboardWizard
          onClose={() => setWizard(false)}
          onDone={async (id) => {
            setWizard(false);
            setError(null);
            await load();
            setOpen(id);
          }}
        />
      )}

      <div className="panel">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Legal name</th>
              <th>City</th>
              <th style={{ textAlign: 'right' }}>Products</th>
              <th style={{ textAlign: 'right' }}>Datasets</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} style={{ opacity: c.active ? 1 : 0.55 }}>
                <td>
                  <button
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      color: 'var(--accent)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 'inherit',
                    }}
                    onClick={() => setOpen(c.id)}
                  >
                    {/*
                      The pill is the link. This page lists every customer, so
                      it is where the tint-to-name mapping is learned — the
                      legend for every pill elsewhere in the app.
                    */}
                    <CustomerPill name={c.name} hue={c.colorHue} title={`Open ${c.name}`} />
                  </button>
                  {!c.active && <span className="muted"> · retired</span>}
                </td>
                <td className="muted">
                  {(c as unknown as { legalName?: string }).legalName ?? (
                    <span style={{ color: 'var(--warn)' }}>not set — invoices need it</span>
                  )}
                </td>
                <td className="muted">{(c as unknown as { city?: string }).city ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{c.productCount}</td>
                <td style={{ textAlign: 'right' }}>{c.datasetCount}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn sm" onClick={() => setOpen(c.id)}>
                    →
                  </button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  {loading ? 'Loading…' : 'No customers yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
